import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardBody,
    Flex,
    FormControl,
    FormLabel,
    Grid,
    HStack,
    IconButton,
    Input,
    Select,
    Spinner,
    Text,
} from '@chakra-ui/react';
import { FiTrash2, FiZap, FiUser, FiTag, FiSmartphone, FiMessageCircle, FiActivity } from 'react-icons/fi';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { enUS } from 'date-fns/locale';
import API from '../../../helpers/axiosInstance';

const CONDITION_TYPES = [
    { value: 'event',            label: 'Event',            icon: FiZap },
    { value: 'tag',              label: 'Tag',              icon: FiTag },
    { value: 'device_type',      label: 'Device Type',      icon: FiSmartphone },
    { value: 'customer_profile', label: 'Customer Profile', icon: FiUser },
    { value: 'other_contacts',   label: 'Other Contacts',   icon: FiMessageCircle },
    { value: 'interactions',     label: 'Interactions',     icon: FiActivity },
];

const DEVICE_TYPES = [
    { value: 'android', label: 'Android' },
    { value: 'apple',   label: 'Apple' },
];


const CONTACT_OPERATORS = [
    { value: 'is_blank',     label: 'Is Blank' },
    { value: 'is_not_blank', label: 'Is Not Blank' },
];

const TOUCHPOINTS = [
    { value: 'web',    label: 'Website' },
    { value: 'mobile_app', label: 'Mobile App' },
    { value: 'chatbot',    label: 'ChatBot' },
];

const INTERACTION_TYPES = [
    { value: 'visited_more_than', label: 'Visited more than X times in Y days' },
    { value: 'not_visited_in',    label: 'Have not visited in X days' },
    { value: 'visited_and_used',  label: 'Visited AND used another touchpoint' },
];

const OPERATORS = [
    { value: 'equals',                label: 'Equals' },
    { value: 'not_equals',            label: 'Not Equals' },
    { value: 'greater_than',          label: 'Greater Than' },
    { value: 'less_than',             label: 'Less Than' },
    { value: 'greater_than_or_equal', label: 'Greater Than or Equal' },
    { value: 'less_than_or_equal',    label: 'Less Than or Equal' },
    { value: 'contains',              label: 'Contains' },
    { value: 'contains_any',          label: 'Contains Any Of' },
    { value: 'starts_with',           label: 'Starts With' },
    { value: 'ends_with',             label: 'Ends With' },
    { value: 'in_list',               label: 'In List' },
    { value: 'not_in_list',           label: 'Not In List' },
];

// Operators whose value is a comma-separated list (rendered/sent as an array).
export const LIST_OPERATORS = ['in_list', 'not_in_list', 'contains_any'];

const PROFILE_OPERATORS = [
    { value: 'equals',      label: 'Equals' },
    { value: 'not_equals',  label: 'Not Equals' },
    { value: 'contains',    label: 'Contains' },
    { value: 'starts_with', label: 'Starts With' },
    { value: 'ends_with',   label: 'Ends With' },
];

const getApiHeaders = () => ({
    'Content-Type': 'application/json',
    'x-api-key': localStorage.getItem('api-key'),
    'x-access-token': localStorage.getItem('customer-portal'),
});

export const CohortCondition = ({
    condition,
    conditionIndex,
    onUpdate,
    onDelete,
    canDelete,
    events,
    eventAttributes,
    onEventChange,
    isLoadingAttributes,
}) => {
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [dateRange, setDateRange] = useState([{ startDate: new Date(), endDate: new Date(), key: 'selection' }]);
    const [tags, setTags] = useState([]);
    const [loadingTags, setLoadingTags] = useState(false);
    const [profileKeys, setProfileKeys] = useState([]);
    const [loadingProfileKeys, setLoadingProfileKeys] = useState(false);
    const [contactTypes, setContactTypes] = useState([]);
    const [loadingContactTypes, setLoadingContactTypes] = useState(false);

    useEffect(() => {
        if (condition.time_frame === 'custom_range') {
            if (condition.custom_date_range) {
                setDateRange([{
                    startDate: new Date(condition.custom_date_range.start_date),
                    endDate: new Date(condition.custom_date_range.end_date),
                    key: 'selection',
                }]);
            }
            setShowDatePicker(true);
        } else {
            setShowDatePicker(false);
        }
    }, [condition.time_frame, condition.custom_date_range]);

    useEffect(() => {
        if (condition.type === 'tag' && tags.length === 0) fetchTags();
        if (condition.type === 'customer_profile' && profileKeys.length === 0) fetchProfileKeys();
        if (condition.type === 'other_contacts' && contactTypes.length === 0) fetchContactTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [condition.type]);

    const fetchTags = async () => {
        setLoadingTags(true);
        try {
            const res = await API.get(`${process.env.REACT_APP_API_URL}/tags`, { headers: getApiHeaders() });
            const raw = res.data?.data;
            setTags(Array.isArray(raw) ? raw : (raw?.tags || []));
        } catch {
            setTags([]);
        } finally {
            setLoadingTags(false);
        }
    };

    const fetchProfileKeys = async () => {
        setLoadingProfileKeys(true);
        try {
            const res = await API.get(`${process.env.REACT_APP_API_URL}/contacts/customer-profile-keys`, { headers: getApiHeaders() });
            const raw = res.data?.data;
            setProfileKeys(Array.isArray(raw) ? raw : (raw?.keys || []));
        } catch {
            setProfileKeys([]);
        } finally {
            setLoadingProfileKeys(false);
        }
    };

    const fetchContactTypes = async () => {
        setLoadingContactTypes(true);
        try {
            const res = await API.get(`${process.env.REACT_APP_API_URL}/contacts/other-contact-types`, { headers: getApiHeaders() });
            const raw = res.data?.data;
            setContactTypes(Array.isArray(raw) ? raw : (raw?.types || []));
        } catch {
            setContactTypes([]);
        } finally {
            setLoadingContactTypes(false);
        }
    };

    const handleTypeChange = (newType) => {
        const base = { id: condition.id, type: newType };
        const defaults = {
            event:            { ...base, event: '', property: '', operator: 'equals', value: '', time_frame: 'all_time' },
            tag:              { ...base, tag: '' },
            device_type:      { ...base, device_type: '' },
            customer_profile: { ...base, profile_key: '', operator: 'equals', value: '' },
            other_contacts:   { ...base, contact_type: '', condition: '' },
            interactions:     { ...base, touchpoint: '', interaction_type: '', interactionCount: '', interactionDays: '', value: '' },
        };
        onUpdate(defaults[newType] || base);

        if (newType === 'tag' && tags.length === 0) fetchTags();
        if (newType === 'customer_profile' && profileKeys.length === 0) fetchProfileKeys();
        if (newType === 'other_contacts' && contactTypes.length === 0) fetchContactTypes();
    };

    const handleFieldChange = (field, value) => {
        const updated = { ...condition, [field]: value };

        if (field === 'event') {
            updated.property = '';
            updated.value = '';
            onEventChange(value, condition.id);
        }
        if (field === 'contact_type') updated.condition = '';
        if (field === 'touchpoint') updated.interaction_type = '';
        if (field === 'interaction_type') {
            updated.interactionCount = '';
            updated.interactionDays = '';
            updated.value = '';
        }
        if (field === 'time_frame') {
            if (value === 'custom_range') {
                setShowDatePicker(true);
            } else {
                setShowDatePicker(false);
                delete updated.custom_date_range;
            }
        }

        onUpdate(updated);
    };

    // Event condition "mode": property | count | exclude.
    // Derived from the stored shape; changing it resets the relevant fields.
    const COUNT_OPERATORS = ['greater_than_or_equal', 'greater_than', 'less_than', 'less_than_or_equal', 'equals', 'not_equals'];
    const eventMode = condition.did_not_perform ? 'exclude' : (condition.property === 'count' ? 'count' : 'property');
    const handleModeChange = (mode) => {
        const updated = { ...condition };
        if (mode === 'exclude') {
            updated.did_not_perform = true;
            updated.property = '';
            updated.operator = 'equals';
            updated.value = '';
        } else if (mode === 'count') {
            updated.did_not_perform = false;
            updated.property = 'count';
            if (!COUNT_OPERATORS.includes(updated.operator)) updated.operator = 'greater_than_or_equal';
            if (updated.value === '' || updated.value === undefined || updated.value === null) updated.value = 1;
        } else { // property
            updated.did_not_perform = false;
            if (updated.property === 'count') updated.property = '';
            if (!updated.operator) updated.operator = 'equals';
        }
        onUpdate(updated);
    };

    const toLocalISOString = (date) => {
        const tzOffset = date.getTimezoneOffset() * 60000;
        return new Date(date - tzOffset).toISOString().slice(0, -1);
    };

    const handleDateChange = (ranges) => {
        const selected = ranges.selection;
        setDateRange([selected]);
        onUpdate({
            ...condition,
            custom_date_range: {
                start_date: toLocalISOString(new Date(selected.startDate)),
                end_date: toLocalISOString(new Date(selected.endDate)),
            },
        });
    };

    const getConditionPreview = () => {
        switch (condition.type) {
            case 'event': {
                const ev = condition.event || 'Event';
                const tf = condition.time_frame?.replace(/_/g, ' ') || 'all time';
                const dateInfo = condition.time_frame === 'custom_range' && condition.custom_date_range
                    ? ` (${new Date(condition.custom_date_range.start_date).toLocaleDateString()} → ${new Date(condition.custom_date_range.end_date).toLocaleDateString()})`
                    : '';
                const hasProp = condition.property && condition.property !== '';
                // Exclusion ("did NOT perform") — clean phrasing, no Property=""
                if (condition.did_not_perform) {
                    const filter = hasProp
                        ? ` where ${condition.property} ${condition.operator?.replace(/_/g, ' ') || 'equals'} "${condition.value || ''}"`
                        : '';
                    return `Did NOT perform ${ev}${filter} (${tf})${dateInfo}`;
                }
                // Count — "performed Event >= N times"
                if (condition.property === 'count') {
                    const op = condition.operator?.replace(/_/g, ' ') || 'at least';
                    const n = (condition.value === '' || condition.value === undefined || condition.value === null) ? 'N' : condition.value;
                    return `Performed ${ev} ${op} ${n} times (${tf})${dateInfo}`;
                }
                const prop = condition.property || 'Property';
                const op = condition.operator?.replace(/_/g, ' ') || 'equals';
                const val = condition.value || '""';
                return `${ev} > ${prop} ${op} "${val}" (${tf})${dateInfo}`;
            }
            case 'tag':
                return `Tag: ${condition.tag || 'undefined'}`;
            case 'device_type': {
                const dt = DEVICE_TYPES.find(d => d.value === condition.device_type)?.label || condition.device_type;
                return `Device Type: ${dt || 'undefined'}`;
            }
            case 'customer_profile': {
                const key = condition.profile_key || 'Key';
                const op = condition.operator?.replace(/_/g, ' ') || 'equals';
                const val = condition.value || '""';
                return `${key} ${op} "${val}"`;
            }
            case 'other_contacts': {
                const ct = contactTypes.find(c => String(c.id) === String(condition.contact_type))?.name || condition.contact_type || 'Contact';
                const op = CONTACT_OPERATORS.find(o => o.value === condition.condition)?.label || condition.condition || '';
                return `${ct} ${op}`.trim();
            }
            case 'interactions': {
                if (!condition.touchpoint) return '';
                const tp = TOUCHPOINTS.find(t => t.value === condition.touchpoint)?.label || condition.touchpoint;
                if (!condition.interaction_type) return `${tp} → ...`;
                switch (condition.interaction_type) {
                    case 'visited_more_than':
                        return `${tp}: visited more than ${condition.interactionCount || 'X'} times in ${condition.interactionDays || 'Y'} days`;
                    case 'not_visited_in':
                        return `${tp}: not visited in ${condition.interactionDays || 'X'} days`;
                    case 'visited_and_used': {
                        const second = TOUCHPOINTS.find(t => t.value === condition.value)?.label || condition.value || '...';
                        return `${tp} AND used ${second}`;
                    }
                    default:
                        return `${tp} → ${condition.interaction_type}`;
                }
            }
            default:
                return '';
        }
    };

    const typeConfig = CONDITION_TYPES.find(t => t.value === condition.type) || CONDITION_TYPES[0];
    const TypeIcon = typeConfig.icon;
    const currentEventAttributes = eventAttributes[condition.event] || [];
    const preview = getConditionPreview();

    const TimeFrameSelect = (
        <Select
            value={condition.time_frame || 'all_time'}
            onChange={(e) => handleFieldChange('time_frame', e.target.value)}
            bg="white"
        >
            <option value="all_time">All Time</option>
            <option value="last_24_hours">Last 24 Hours</option>
            <option value="last_7_days">Last 7 Days</option>
            <option value="last_30_days">Last 30 Days</option>
            <option value="last_90_days">Last 90 Days</option>
            <option value="last_year">Last Year</option>
            <option value="custom_range">Custom Date</option>
        </Select>
    );

    return (
        <Card mb={4} border="1px" borderColor="gray.200">
            <CardBody>
                {/* Header */}
                <Flex justify="space-between" align="center" mb={4}>
                    <HStack>
                        <Box color="blue.500"><TypeIcon /></Box>
                        <Text fontWeight="semibold">Condition {conditionIndex + 1}</Text>
                    </HStack>
                    {canDelete && (
                        <IconButton
                            icon={<FiTrash2 />}
                            size="sm"
                            colorScheme="red"
                            variant="ghost"
                            onClick={() => onDelete(condition.id)}
                            aria-label="Delete condition"
                        />
                    )}
                </Flex>

                {/* Row 1: Condition Type + Primary field */}
                <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={4}>
                    <FormControl>
                        <FormLabel fontSize="sm" fontWeight="medium">Condition Type</FormLabel>
                        <Select
                            value={condition.type}
                            onChange={(e) => handleTypeChange(e.target.value)}
                            bg="white"
                        >
                            {CONDITION_TYPES.map(ct => (
                                <option key={ct.value} value={ct.value}>{ct.label}</option>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Event */}
                    {condition.type === 'event' && (
                        <FormControl>
                            <FormLabel fontSize="sm" fontWeight="medium">Event</FormLabel>
                            <Select
                                value={condition.event}
                                onChange={(e) => handleFieldChange('event', e.target.value)}
                                bg="white"
                                placeholder="Select event"
                            >
                                {/* LOCAL ONLY — if a prefilled event isn't in the
                                    fetched events list yet, still show it so the
                                    dropdown reflects the selected value. */}
                                {condition.event && !events.some(ev => ev.name === condition.event) && (
                                    <option key={`__prefill__${condition.event}`} value={condition.event}>
                                        {condition.event}
                                    </option>
                                )}
                                {events.map(ev => (
                                    <option key={ev.name} value={ev.name}>{ev.display_name}</option>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {/* Tag */}
                    {condition.type === 'tag' && (
                        <FormControl>
                            <FormLabel fontSize="sm" fontWeight="medium">Tag</FormLabel>
                            <Select
                                value={condition.tag || ''}
                                onChange={(e) => handleFieldChange('tag', e.target.value)}
                                bg="white"
                                placeholder={loadingTags ? 'Loading...' : 'Select tag'}
                                disabled={loadingTags}
                            >
                                {tags.map((tag) => (
                                    <option key={tag._id || tag.name || tag} value={tag._id || tag}>{tag.name || tag}</option>
                                ))}
                            </Select>
                            {loadingTags && (
                                <HStack mt={1}>
                                    <Spinner size="xs" />
                                    <Text fontSize="xs" color="gray.500">Loading tags...</Text>
                                </HStack>
                            )}
                        </FormControl>
                    )}

                    {/* Device Type */}
                    {condition.type === 'device_type' && (
                        <FormControl>
                            <FormLabel fontSize="sm" fontWeight="medium">Device Type</FormLabel>
                            <Select
                                value={condition.device_type || ''}
                                onChange={(e) => handleFieldChange('device_type', e.target.value)}
                                bg="white"
                                placeholder="Select device type"
                            >
                                {DEVICE_TYPES.map(d => (
                                    <option key={d.value} value={d.value}>{d.label}</option>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {/* Customer Profile */}
                    {condition.type === 'customer_profile' && (
                        <FormControl>
                            <FormLabel fontSize="sm" fontWeight="medium">Profile Key</FormLabel>
                            <Select
                                value={condition.profile_key || ''}
                                onChange={(e) => handleFieldChange('profile_key', e.target.value)}
                                bg="white"
                                placeholder={loadingProfileKeys ? 'Loading...' : 'Select profile key'}
                                disabled={loadingProfileKeys}
                            >
                                {profileKeys.map((pk) => (
                                    <option key={pk._id || pk.key} value={pk.key}>{pk.display_name || pk.key}</option>
                                ))}
                            </Select>
                            {loadingProfileKeys && (
                                <HStack mt={1}>
                                    <Spinner size="xs" />
                                    <Text fontSize="xs" color="gray.500">Loading keys...</Text>
                                </HStack>
                            )}
                        </FormControl>
                    )}

                    {/* Other Contacts */}
                    {condition.type === 'other_contacts' && (
                        <FormControl>
                            <FormLabel fontSize="sm" fontWeight="medium">Contact Type</FormLabel>
                            <Select
                                value={condition.contact_type || ''}
                                onChange={(e) => handleFieldChange('contact_type', e.target.value)}
                                bg="white"
                                placeholder={loadingContactTypes ? 'Loading...' : 'Select contact type'}
                                disabled={loadingContactTypes}
                            >
                                {contactTypes.map((ct) => (
                                    <option key={ct.id} value={ct.id}>{ct.name}</option>
                                ))}
                            </Select>
                            {loadingContactTypes && (
                                <HStack mt={1}>
                                    <Spinner size="xs" />
                                    <Text fontSize="xs" color="gray.500">Loading contact types...</Text>
                                </HStack>
                            )}
                        </FormControl>
                    )}

                    {/* Interactions */}
                    {condition.type === 'interactions' && (
                        <FormControl>
                            <FormLabel fontSize="sm" fontWeight="medium">Touchpoint</FormLabel>
                            <Select
                                value={condition.touchpoint || ''}
                                onChange={(e) => handleFieldChange('touchpoint', e.target.value)}
                                bg="white"
                                placeholder="Select touchpoint"
                            >
                                {TOUCHPOINTS.map(t => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                </Grid>

                {/* Row 2: Secondary fields per type */}

                {/* Event: Condition Mode dropdown drives which fields show.
                    - property  → Property + Operator + Value + Time Frame
                    - count     → Operator + Value (number) + Time Frame
                    - exclude   → Time Frame only (Did NOT perform), optional property filter retained */}
                {condition.type === 'event' && (
                    <>
                        <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={4}>
                            <FormControl>
                                <FormLabel fontSize="sm" fontWeight="medium">Condition Mode</FormLabel>
                                <Select
                                    value={eventMode}
                                    onChange={(e) => handleModeChange(e.target.value)}
                                    bg="white"
                                >
                                    <option value="property">Property Condition</option>
                                    <option value="count">Count</option>
                                    <option value="exclude">Did NOT perform</option>
                                </Select>
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="sm" fontWeight="medium">Time Frame</FormLabel>
                                {TimeFrameSelect}
                            </FormControl>
                        </Grid>

                        {/* Property Condition mode → Property + Operator + Value */}
                        {eventMode === 'property' && (
                            <>
                                <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={4}>
                                    <FormControl>
                                        <FormLabel fontSize="sm" fontWeight="medium">Property</FormLabel>
                                        <Select
                                            value={condition.property || ''}
                                            onChange={(e) => handleFieldChange('property', e.target.value)}
                                            bg="white"
                                            placeholder={isLoadingAttributes ? 'Loading...' : 'Select property'}
                                            disabled={!condition.event || isLoadingAttributes}
                                        >
                                            {condition.property &&
                                             condition.property !== 'count' &&
                                             !currentEventAttributes.some(a => a.name === condition.property) && (
                                                <option key={`__prefill__${condition.property}`} value={condition.property}>
                                                    {condition.property} (prefilled)
                                                </option>
                                            )}
                                            {currentEventAttributes.map(attr => (
                                                <option key={attr.name} value={attr.name}>{attr.name} ({attr.type})</option>
                                            ))}
                                        </Select>
                                        {isLoadingAttributes && (
                                            <HStack mt={1}>
                                                <Spinner size="xs" />
                                                <Text fontSize="xs" color="gray.500">Loading properties...</Text>
                                            </HStack>
                                        )}
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel fontSize="sm" fontWeight="medium">Operator</FormLabel>
                                        <Select
                                            value={condition.operator}
                                            onChange={(e) => handleFieldChange('operator', e.target.value)}
                                            bg="white"
                                        >
                                            {OPERATORS.map(op => (
                                                <option key={op.value} value={op.value}>{op.label}</option>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <FormControl>
                                    <FormLabel fontSize="sm" fontWeight="medium">Value</FormLabel>
                                    <Input
                                        value={condition.value || ''}
                                        onChange={(e) => handleFieldChange('value', e.target.value)}
                                        placeholder="Enter value"
                                        bg="white"
                                    />
                                </FormControl>
                            </>
                        )}

                        {/* Count mode → number of times the event occurred */}
                        {eventMode === 'count' && (
                            <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={4}>
                                <FormControl>
                                    <FormLabel fontSize="sm" fontWeight="medium">Operator</FormLabel>
                                    <Select
                                        value={condition.operator}
                                        onChange={(e) => handleFieldChange('operator', e.target.value)}
                                        bg="white"
                                    >
                                        {OPERATORS.filter(op => COUNT_OPERATORS.includes(op.value)).map(op => (
                                            <option key={op.value} value={op.value}>{op.label}</option>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontSize="sm" fontWeight="medium">Number of Times</FormLabel>
                                    <Input
                                        type="number"
                                        min={0}
                                        value={condition.value === undefined || condition.value === null ? '' : condition.value}
                                        onChange={(e) => handleFieldChange('value', e.target.value)}
                                        placeholder="e.g. 3"
                                        bg="white"
                                    />
                                </FormControl>
                            </Grid>
                        )}

                        {/* Did NOT perform mode → optional property filter + helper text */}
                        {eventMode === 'exclude' && (
                            <>
                                {(condition.property && condition.property !== '' && condition.property !== 'count') && (
                                    <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={4}>
                                        <FormControl>
                                            <FormLabel fontSize="sm" fontWeight="medium">Operator</FormLabel>
                                            <Select
                                                value={condition.operator}
                                                onChange={(e) => handleFieldChange('operator', e.target.value)}
                                                bg="white"
                                            >
                                                {OPERATORS.map(op => (
                                                    <option key={op.value} value={op.value}>{op.label}</option>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel fontSize="sm" fontWeight="medium">Value</FormLabel>
                                            <Input
                                                value={condition.value || ''}
                                                onChange={(e) => handleFieldChange('value', e.target.value)}
                                                placeholder="Enter value"
                                                bg="white"
                                            />
                                        </FormControl>
                                    </Grid>
                                )}
                                <Text fontSize="xs" color="gray.500" mb={2}>
                                    {condition.property && condition.property !== '' && condition.property !== 'count'
                                        ? `Excludes customers who performed “${condition.event || 'this event'}” where ${condition.property} ${OPERATORS.find(o => o.value === condition.operator)?.label || condition.operator} ${condition.value || '…'}.`
                                        : `Excludes customers who performed “${condition.event || 'this event'}”.`}
                                </Text>
                            </>
                        )}
                    </>
                )}

                {/* Customer Profile: Operator + Value */}
                {condition.type === 'customer_profile' && (
                    <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={4}>
                        <FormControl>
                            <FormLabel fontSize="sm" fontWeight="medium">Operator</FormLabel>
                            <Select
                                value={condition.operator || 'equals'}
                                onChange={(e) => handleFieldChange('operator', e.target.value)}
                                bg="white"
                            >
                                {PROFILE_OPERATORS.map(op => (
                                    <option key={op.value} value={op.value}>{op.label}</option>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl>
                            <FormLabel fontSize="sm" fontWeight="medium">Value</FormLabel>
                            <Input
                                value={condition.value || ''}
                                onChange={(e) => handleFieldChange('value', e.target.value)}
                                placeholder="Enter value (e.g., New York, Male, 25-34)"
                                bg="white"
                            />
                        </FormControl>
                    </Grid>
                )}

                {/* Other Contacts: Condition (shown after contact_type selected) */}
                {condition.type === 'other_contacts' && condition.contact_type && (
                    <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={4}>
                        <FormControl>
                            <FormLabel fontSize="sm" fontWeight="medium">Condition</FormLabel>
                            <Select
                                value={condition.condition || ''}
                                onChange={(e) => handleFieldChange('condition', e.target.value)}
                                bg="white"
                                placeholder="Select condition"
                            >
                                {CONTACT_OPERATORS.map(op => (
                                    <option key={op.value} value={op.value}>{op.label}</option>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                )}

                {/* Interactions: Interaction Type (shown after touchpoint selected) */}
                {condition.type === 'interactions' && condition.touchpoint && (
                    <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={4}>
                        <FormControl>
                            <FormLabel fontSize="sm" fontWeight="medium">Interaction Type</FormLabel>
                            <Select
                                value={condition.interaction_type || ''}
                                onChange={(e) => handleFieldChange('interaction_type', e.target.value)}
                                bg="white"
                                placeholder="Select interaction type"
                            >
                                {INTERACTION_TYPES.map(t => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                )}

                {/* visited_more_than: Number of Visits + Within Days */}
                {condition.type === 'interactions' && condition.interaction_type === 'visited_more_than' && (
                    <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={4}>
                        <FormControl>
                            <FormLabel fontSize="sm" fontWeight="medium">Number of Visits</FormLabel>
                            <Input
                                type="number"
                                value={condition.interactionCount || ''}
                                onChange={(e) => handleFieldChange('interactionCount', e.target.value)}
                                placeholder="e.g., 5"
                                bg="white"
                                min={1}
                            />
                        </FormControl>
                        <FormControl>
                            <FormLabel fontSize="sm" fontWeight="medium">Within Days</FormLabel>
                            <Input
                                type="number"
                                value={condition.interactionDays || ''}
                                onChange={(e) => handleFieldChange('interactionDays', e.target.value)}
                                placeholder="e.g., 30"
                                bg="white"
                                min={1}
                            />
                        </FormControl>
                    </Grid>
                )}

                {/* not_visited_in: Number of Days */}
                {condition.type === 'interactions' && condition.interaction_type === 'not_visited_in' && (
                    <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={4}>
                        <FormControl>
                            <FormLabel fontSize="sm" fontWeight="medium">Number of Days</FormLabel>
                            <Input
                                type="number"
                                value={condition.interactionDays || ''}
                                onChange={(e) => handleFieldChange('interactionDays', e.target.value)}
                                placeholder="e.g., 14"
                                bg="white"
                                min={1}
                            />
                        </FormControl>
                    </Grid>
                )}

                {/* visited_and_used: Second touchpoint select */}
                {condition.type === 'interactions' && condition.interaction_type === 'visited_and_used' && (
                    <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={4}>
                        <FormControl>
                            <FormLabel fontSize="sm" fontWeight="medium">Another Touchpoint</FormLabel>
                            <Select
                                value={condition.value || ''}
                                onChange={(e) => handleFieldChange('value', e.target.value)}
                                bg="white"
                                placeholder="Select another touchpoint"
                            >
                                {TOUCHPOINTS.filter(t => t.value !== condition.touchpoint).map(t => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                )}

                {/* Date Picker */}
                {showDatePicker && (
                    <Box mb={4}>
                        <DateRange
                            editableDateInputs
                            onChange={handleDateChange}
                            moveRangeOnFirstSelection={false}
                            ranges={dateRange}
                            rangeColors={['var(--brand-primary)']}
                            locale={enUS}
                        />
                    </Box>
                )}

                {/* Condition Preview */}
                {preview && (
                    <Box bg="gray.50" p={3} borderRadius="md" border="1px" borderColor="gray.200">
                        <Text fontSize="sm" fontWeight="semibold" mb={1} color="gray.700">
                            Condition Preview
                        </Text>
                        <Text fontSize="sm" fontFamily="mono" color="gray.800">
                            {preview}
                        </Text>
                    </Box>
                )}
            </CardBody>
        </Card>
    );
};
